import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  listAllCategories,
  listAllProducts,
  updateCategory,
  deleteCategory,
  deleteProduct,
  reorderProducts,
} from "@/lib/admin.functions";
import { toast } from "sonner";
import { Loader2, Save, Trash2 } from "lucide-react";
import { PageHeader, Field, inp, slugify, type Cat, type Prod } from "@/components/admin/shared";
import { SortableProductList } from "@/components/admin/SortableProductList";

export const Route = createFileRoute("/admin/categories/$id")({
  component: EditCategoryPage,
});

function EditCategoryPage() {
  const { id } = Route.useParams();
  const list = useServerFn(listAllCategories);
  const listProds = useServerFn(listAllProducts);
  const update = useServerFn(updateCategory);
  const del = useServerFn(deleteCategory);
  const delProd = useServerFn(deleteProduct);
  const reorder = useServerFn(reorderProducts);
  const navigate = useNavigate();
  const [cat, setCat] = useState<Cat | null>(null);
  const [allProducts, setAllProducts] = useState<Prod[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reloadProducts = async () => {
    try {
      setAllProducts((await listProds({})) as unknown as Prod[]);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    Promise.all([list({}), listProds({})])
      .then(([cs, ps]) => {
        const c = (cs as Cat[]).find((x) => x.id === id);
        if (!c) throw new Error("Kategorin hittades inte");
        setCat(c);
        setAllProducts(ps as unknown as Prod[]);
      })
      .catch((e: any) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cat) return;
    setBusy(true);
    try {
      await update({
        data: {
          id: cat.id,
          patch: {
            slug: cat.slug,
            name: cat.name,
            description: cat.description,
            sort_order: cat.sort_order,
            is_published: cat.is_published,
          },
        },
      });
      toast.success("Sparad");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!cat) return;
    if (!confirm("Ta bort kategorin?")) return;
    try {
      await del({ data: { id: cat.id } });
      toast.success("Borttagen");
      navigate({ to: "/admin/categories" });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!cat) return <p className="text-sm text-muted-foreground">Hittades inte.</p>;

  return (
    <>
      <PageHeader title={`Redigera: ${cat.name}`} backTo="/admin/categories">
        <button
          onClick={remove}
          className="inline-flex items-center gap-2 rounded-full bg-destructive text-destructive-foreground px-4 py-2 text-sm font-bold"
        >
          <Trash2 className="h-4 w-4" /> Ta bort
        </button>
      </PageHeader>

      <form
        onSubmit={save}
        className="rounded-2xl border border-border bg-card p-6 grid sm:grid-cols-2 gap-4"
      >
        <Field label="Namn *">
          <input
            value={cat.name}
            onChange={(e) => setCat({ ...cat, name: e.target.value })}
            className={inp}
            required
          />
        </Field>
        <Field label="Slug *">
          <div className="flex gap-2">
            <input
              value={cat.slug}
              onChange={(e) => setCat({ ...cat, slug: e.target.value })}
              className={inp}
              required
            />
            <button
              type="button"
              onClick={() => setCat({ ...cat, slug: slugify(cat.name) })}
              className="rounded-lg bg-secondary px-3 text-xs whitespace-nowrap"
            >
              Auto
            </button>
          </div>
        </Field>
        <Field label="Beskrivning" full>
          <textarea
            value={cat.description ?? ""}
            onChange={(e) => setCat({ ...cat, description: e.target.value })}
            className={`${inp} min-h-[80px]`}
          />
        </Field>
        <Field label="Sortering">
          <input
            type="number"
            value={cat.sort_order}
            onChange={(e) =>
              setCat({ ...cat, sort_order: parseInt(e.target.value) || 0 })
            }
            className={inp}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm self-end pb-2">
          <input
            type="checkbox"
            checked={cat.is_published}
            onChange={(e) => setCat({ ...cat, is_published: e.target.checked })}
          />{" "}
          Publicerad
        </label>
        <div className="sm:col-span-2 flex justify-end">
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-bold disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}{" "}
            Spara
          </button>
        </div>
      </form>

      <CategoryProducts
        categorySlug={cat.slug}
        allProducts={allProducts}
        onReorder={async (ids) => {
          // Optimistic: bump only this category's products in local state
          const map = new Map(allProducts.map((p) => [p.id!, p]));
          const reordered = ids.map((id, i) => ({ ...(map.get(id) as Prod), sort_order: i }));
          const others = allProducts.filter((p) => p.category_slug !== cat.slug);
          setAllProducts([...others, ...reordered]);
          try {
            await reorder({ data: { ids } });
          } catch (e: any) {
            toast.error(`Kunde inte spara ordning: ${e.message}`);
            await reloadProducts();
          }
        }}
        onDelete={async (pid) => {
          if (!confirm("Ta bort produkten?")) return;
          try {
            await delProd({ data: { id: pid } });
            toast.success("Borttagen");
            await reloadProducts();
          } catch (e: any) {
            toast.error(e.message);
          }
        }}
      />
    </>
  );
}

function CategoryProducts({
  categorySlug,
  allProducts,
  onReorder,
  onDelete,
}: {
  categorySlug: string;
  allProducts: Prod[];
  onReorder: (ids: string[]) => void | Promise<void>;
  onDelete: (id: string) => void;
}) {
  const inCat = allProducts
    .filter((p) => p.category_slug === categorySlug)
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="mt-8">
      <h3 className="font-display text-xl font-extrabold mb-2">
        Produkter i kategorin ({inCat.length})
      </h3>
      {inCat.length === 0 ? (
        <p className="text-sm text-muted-foreground">Inga produkter i denna kategori ännu.</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-3">
            Dra med <span className="font-bold">⋮⋮</span>-handtaget för att ändra ordning på hur produkterna visas i kategorin.
          </p>
          <SortableProductList products={inCat} onReorder={onReorder} onDelete={onDelete} />
        </>
      )}
    </div>
  );
}
